import type { TargetReviewsResponse } from "@shared/types";
import {
	Award,
	Briefcase,
	Building2,
	Lock,
	ShieldCheck,
	X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReviewSummarySection } from "@/components/ui/review-summary-section";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { resolveMediaUrl } from "@/lib/utils";
import { fetchDesignerReviews } from "@/services/reviews-api.service";
import {
	getPublicPortfolio,
	getPublicPortfolioProject,
	type PublicPortfolioProjectResponse,
	type PublicPortfolioResponse,
} from "../services/portfolio-api.service";
import {
	PORTFOLIO_TEMPLATES,
	renderPublicTemplateSection,
} from "../template-definitions";

function ProjectDetailsModal({
	data,
	onClose,
	onRequireAuth,
}: {
	data: PublicPortfolioProjectResponse;
	onClose: () => void;
	onRequireAuth: () => void;
}): React.ReactElement {
	const project = data.project;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
			<button
				type="button"
				aria-label="Close project details"
				className="absolute inset-0"
				onClick={onClose}
			/>
			{/* Modal panel */}
			<div
				role="dialog"
				aria-modal="true"
				style={{
					position: "relative",
					width: "100%",
					maxWidth: 760,
					maxHeight: "90vh",
					overflowY: "auto",
					background: "var(--color-dashboard-surface)",
					borderRadius: 14,
					boxShadow: "0 32px 80px rgba(30,41,59,0.32)",
				}}
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{/* Header image with gradient overlay */}
				<div style={{ position: "relative" }}>
					<img
						src={resolveMediaUrl(project.thumbnailUrl)}
						alt={project.title}
						style={{
							width: "100%",
							maxHeight: 240,
							objectFit: "cover",
							borderRadius: "14px 14px 0 0",
						}}
					/>
					<div
						style={{
							position: "absolute",
							inset: 0,
							background:
								"linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 50%, transparent 100%)",
							borderRadius: "14px 14px 0 0",
						}}
					/>
					{/* Close button */}
					<button
						type="button"
						onClick={onClose}
						style={{
							position: "absolute",
							top: 14,
							right: 14,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 34,
							height: 34,
							borderRadius: "50%",
							background: "rgba(30,41,59,0.55)",
							color: "var(--color-dashboard-on-fill)",
							border: "none",
							cursor: "pointer",
							backdropFilter: "blur(4px)",
						}}
					>
						<X className="h-4 w-4" />
					</button>
					{/* Title overlay */}
					<div
						style={{
							position: "absolute",
							bottom: 18,
							left: 24,
							right: 24,
						}}
					>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 700,
								fontSize: 22,
								color: "var(--color-dashboard-on-fill)",
								letterSpacing: "-0.02em",
							}}
						>
							{project.title}
						</h2>
						<p
							style={{
								marginTop: 4,
								fontSize: 13,
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 75%, transparent)",
								fontFamily: "var(--font-body)",
							}}
						>
							{project.shortDescription}
						</p>
					</div>
				</div>

				{/* Body */}
				<div
					style={{
						padding: "24px 28px",
						display: "flex",
						flexDirection: "column",
						gap: 20,
					}}
				>
					{data.previewOnly ? (
						<div
							style={{
								background: "var(--color-dashboard-divider)",
								borderRadius: 10,
								padding: "28px 24px",
								textAlign: "center",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 12,
							}}
						>
							<div
								style={{
									width: 48,
									height: 48,
									borderRadius: "50%",
									background: "var(--color-dashboard-dark)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Lock
									className="h-5 w-5"
									style={{ color: "var(--color-dashboard-on-fill)" }}
								/>
							</div>
							<p
								style={{
									fontWeight: 600,
									fontSize: 15,
									color: "var(--color-dashboard-text)",
									fontFamily: "var(--font-display)",
								}}
							>
								Sign up to view full project details.
							</p>
							<p
								style={{
									fontSize: 13,
									color: "var(--color-dashboard-muted)",
									fontFamily: "var(--font-body)",
								}}
							>
								Full gallery, detailed notes, category, and completion details
								are available after login.
							</p>
							<div
								style={{
									display: "flex",
									gap: 10,
									flexWrap: "wrap",
									justifyContent: "center",
								}}
							>
								<StudioButton
									variant="primary"
									size="sm"
									onClick={onRequireAuth}
								>
									Create Account
								</StudioButton>
								<StudioButton variant="quiet" size="sm" onClick={onRequireAuth}>
									Log in
								</StudioButton>
							</div>
						</div>
					) : (
						<>
							{/* Meta chips */}
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: 8,
									background: "var(--color-dashboard-divider)",
									borderRadius: 7,
									padding: "12px 16px",
									fontSize: 13,
									color: "var(--color-dashboard-text)",
									fontFamily: "var(--font-body)",
								}}
							>
								<p>
									<span style={{ fontWeight: 600 }}>Category:</span>{" "}
									{project.category || "Not specified"}
								</p>
								<p>
									<span style={{ fontWeight: 600 }}>Completion:</span>{" "}
									{project.completionDate
										? new Date(project.completionDate).toLocaleDateString(
												"en-IN",
											)
										: "Not specified"}
								</p>
							</div>

							{project.fullDescription ? (
								<p
									style={{
										fontSize: 14,
										color: "var(--color-dashboard-muted-dark)",
										fontFamily: "var(--font-body)",
										lineHeight: 1.65,
									}}
								>
									{project.fullDescription}
								</p>
							) : null}

							{/* Image gallery */}
							<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
								{(project.imageUrls || []).map((url) => (
									<div key={url} className="overflow-hidden rounded-xl">
										<img
											src={resolveMediaUrl(url)}
											alt={project.title}
											className="aspect-square w-full object-cover transition-transform duration-300 hover:scale-110"
										/>
									</div>
								))}
							</div>
						</>
					)}

					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<StudioButton variant="quiet" size="sm" onClick={onClose}>
							Close
						</StudioButton>
					</div>
				</div>
			</div>
		</div>
	);
}

// Wave divider SVG component
function WaveDivider(): React.ReactElement {
	return (
		<svg
			className="absolute bottom-0 left-0 w-full overflow-hidden leading-none"
			viewBox="0 0 1200 120"
			preserveAspectRatio="none"
			style={{ height: "60px", width: "calc(100% + 1.3px)" }}
		>
			<title>Decorative wave divider</title>
			<path
				d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.1,118.92,156.63,69.08,321.39,56.44Z"
				className="fill-dashboard-surface"
			/>
		</svg>
	);
}

function PublicPortfolioHeader({
	designer,
	templateName,
	templateSummary,
	signedIn,
	projectCount,
}: {
	designer: PublicPortfolioResponse["designer"];
	templateName: string;
	templateSummary: string;
	signedIn: boolean;
	projectCount: number;
}): React.ReactElement {
	const profileImageUrl = resolveMediaUrl(designer.profilePictureUrl);
	const initial = (designer.name || "D").trim().charAt(0).toUpperCase() || "D";

	return (
		<div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--color-dashboard-dark)] via-[var(--color-dashboard-dark-elevated)] to-[var(--color-dashboard-dark)] text-dashboard-on-fill shadow-2xl">
			{/* Animated gradient orbs - warm earth tones */}
			<div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 animate-pulse rounded-full bg-gradient-to-br from-[var(--color-dashboard-accent)]/30 to-[var(--color-dashboard-accent-light)]/20 blur-3xl" />
			<div className="pointer-events-none absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-gradient-to-tr from-[var(--color-dashboard-accent)]/20 to-[var(--color-dashboard-accent-dark)]/15 blur-3xl" />
			<div className="pointer-events-none absolute right-1/4 top-1/2 h-40 w-40 rounded-full bg-gradient-to-bl from-[var(--color-dashboard-muted-light)]/20 to-[var(--color-dashboard-muted)]/10 blur-2xl" />

			{/* Subtle grid pattern overlay */}
			<div
				className="pointer-events-none absolute inset-0 opacity-[0.02]"
				style={{
					backgroundImage: `linear-gradient(color-mix(in srgb, var(--color-dashboard-paper) 10%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--color-dashboard-paper) 10%, transparent) 1px, transparent 1px)`,
					backgroundSize: "50px 50px",
				}}
			/>

			<div className="relative px-6 py-8 sm:px-10 sm:py-12 lg:px-16 lg:py-14">
				<div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
					{/* Left section: Profile + Info */}
					<div className="flex items-center gap-5 sm:gap-6">
						{/* Profile image with glowing ring */}
						<div className="relative flex-shrink-0">
							<div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 blur-md opacity-60" />
							<div className="relative">
								{profileImageUrl ? (
									<img
										src={profileImageUrl}
										alt={`${designer.name} profile`}
										className="h-20 w-20 rounded-full border-2 border-dashboard-on-fill/20 object-cover ring-4 ring-dashboard-on-fill/10 sm:h-24 sm:w-24"
									/>
								) : (
									<div
										data-testid="public-profile-initial"
										className="grid h-20 w-20 place-items-center rounded-full border-2 border-dashboard-on-fill/20 bg-gradient-to-br from-slate-700 to-slate-800 text-2xl font-bold ring-4 ring-dashboard-on-fill/10 sm:h-24 sm:w-24 sm:text-3xl"
									>
										{initial}
									</div>
								)}
								{/* Verified badge */}
								{designer.isVerified && (
									<div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[var(--color-dashboard-accent-light)] to-[var(--color-dashboard-accent)] ring-2 ring-[var(--color-dashboard-text)]">
										<ShieldCheck className="h-4 w-4 text-dashboard-on-fill" />
									</div>
								)}
							</div>
						</div>

						{/* Name and bio */}
						<div className="min-w-0">
							<h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
								{designer.name}
							</h1>
							{designer.firmName && (
								<div className="mt-1.5 flex items-center gap-2 text-dashboard-on-fill/70">
									<Building2 className="h-4 w-4 flex-shrink-0" />
									<span className="truncate text-sm font-medium">
										{designer.firmName}
									</span>
								</div>
							)}
							{designer.bio && (
								<p className="mt-2 max-w-md text-sm leading-relaxed text-dashboard-on-fill/60 line-clamp-2">
									{designer.bio}
								</p>
							)}
						</div>
					</div>

					{/* Right section: Stats cards */}
					<div className="flex flex-wrap items-center gap-3 sm:gap-4">
						{/* Template card */}
						<div className="rounded-2xl border border-dashboard-on-fill/10 bg-dashboard-on-fill/5 px-4 py-3 backdrop-blur-sm">
							<p className="text-xs font-medium uppercase tracking-wider text-dashboard-on-fill/50">
								Layout
							</p>
							<p className="mt-0.5 text-sm font-semibold text-dashboard-on-fill">
								{templateName}
							</p>
							<p className="mt-0.5 text-xs text-dashboard-on-fill/60">
								{templateSummary}
							</p>
						</div>

						{/* Projects count card */}
						<div className="rounded-2xl border border-[var(--color-dashboard-accent)]/20 bg-gradient-to-br from-[var(--color-dashboard-accent)]/10 to-[var(--color-dashboard-accent-light)]/5 px-5 py-3 backdrop-blur-sm">
							<div className="flex items-center gap-2">
								<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-dashboard-accent)]/20">
									<Briefcase className="h-4 w-4 text-[var(--color-dashboard-accent-light)]" />
								</div>
								<div>
									<p className="text-2xl font-bold text-dashboard-on-fill">
										{projectCount}
									</p>
									<p className="text-xs text-dashboard-on-fill/60">Projects</p>
								</div>
							</div>
						</div>

						{/* Status badge */}
						<div className="rounded-2xl border border-dashboard-on-fill/10 bg-dashboard-on-fill/5 px-4 py-3 backdrop-blur-sm">
							<div className="flex items-center gap-2">
								{designer.isVerified ? (
									<>
										<Award className="h-5 w-5 text-yellow-400" />
										<span className="text-sm font-medium text-dashboard-on-fill">
											Verified Designer
										</span>
									</>
								) : (
									<span className="text-sm font-medium text-dashboard-on-fill/70">
										Published Portfolio
									</span>
								)}
							</div>
							<p className="mt-1 text-xs text-dashboard-on-fill/50">
								{signedIn ? "Welcome back" : "Sign in for full access"}
							</p>
						</div>
					</div>
				</div>
			</div>

			<WaveDivider />
		</div>
	);
}

export function PublicPortfolioPage(): React.ReactElement {
	const { t } = useTranslation();
	const { slug = "" } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isAuthenticated } = useAuthStore();

	const [loading, setLoading] = useState(true);
	const [portfolio, setPortfolio] = useState<PublicPortfolioResponse | null>(
		null,
	);
	const [selected, setSelected] =
		useState<PublicPortfolioProjectResponse | null>(null);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null,
	);
	const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
	const [reviews, setReviews] = useState<TargetReviewsResponse>({
		summary: { averageRating: null, reviewCount: 0 },
		reviews: [],
	});
	const [reviewsFailed, setReviewsFailed] = useState(false);

	const requestedProjectId = useMemo(
		() => searchParams.get("project"),
		[searchParams],
	);

	useEffect(() => {
		async function load() {
			setLoading(true);
			setReviewsFailed(false);
			const response = await getPublicPortfolio(slug);
			setPortfolio(response);
			if (response) {
				try {
					const reviewResult = await fetchDesignerReviews(response.designer.id);
					setReviews(reviewResult);
				} catch (err) {
					console.error("Failed to fetch designer reviews:", err);
					setReviewsFailed(true);
				}
			}
			setLoading(false);
		}

		if (slug) {
			void load();
		}
	}, [slug]);

	useEffect(() => {
		async function loadProject(projectId: string) {
			setLoadingProjectId(projectId);
			const response = await getPublicPortfolioProject(slug, projectId);
			setLoadingProjectId(null);
			if (response) {
				setSelected(response);
				setSelectedProjectId(projectId);
			} else {
				setSelected(null);
				setSelectedProjectId(null);
				setSearchParams((prev) => {
					const next = new URLSearchParams(prev);
					next.delete("project");
					return next;
				});
			}
		}

		if (requestedProjectId && portfolio) {
			void loadProject(requestedProjectId);
		}
	}, [requestedProjectId, portfolio, setSearchParams, slug]);

	async function openProject(projectId: string) {
		setLoadingProjectId(projectId);
		const response = await getPublicPortfolioProject(slug, projectId);
		setLoadingProjectId(null);

		if (response) {
			setSelected(response);
			setSelectedProjectId(projectId);
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.set("project", projectId);
				return next;
			});
		} else {
			setSelected(null);
			setSelectedProjectId(null);
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.delete("project");
				return next;
			});
		}
	}

	function closeModal() {
		setSelected(null);
		setSelectedProjectId(null);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete("project");
			return next;
		});
	}

	function requireAuth() {
		const projectId = selectedProjectId || requestedProjectId;
		const redirectTo = `/portfolio/${slug}${projectId ? `?project=${projectId}` : ""}`;
		navigate("/login", { state: { redirectTo } });
	}

	if (loading) {
		return (
			<div className="space-y-4 p-4 sm:p-6">
				<Skeleton className="h-40 w-full rounded-2xl" />
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<Skeleton className="h-64 rounded-xl" />
					<Skeleton className="h-64 rounded-xl" />
					<Skeleton className="h-64 rounded-xl" />
				</div>
			</div>
		);
	}

	if (!portfolio) {
		return (
			<div className="min-h-screen bg-transparent p-4 sm:p-12 flex items-center justify-center">
				<div style={{ maxWidth: 520, width: "100%" }}>
					<FloatPanel elevation={1} padding={40}>
						<div className="text-center space-y-3">
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 22,
									color: "var(--color-dashboard-text)",
								}}
							>
								Portfolio not found
							</h2>
							<p
								style={{
									fontSize: 14,
									color: "var(--color-dashboard-muted)",
									fontFamily: "var(--font-body)",
								}}
							>
								The link may be invalid or this portfolio is not published.
							</p>
						</div>
					</FloatPanel>
				</div>
			</div>
		);
	}

	const templateDef =
		PORTFOLIO_TEMPLATES.find(
			(item) => item.id === portfolio.portfolio.template,
		) || PORTFOLIO_TEMPLATES[0];

	return (
		<div className="min-h-screen bg-gradient-to-b from-dashboard-surface via-dashboard-surface to-background p-4 sm:p-6">
			<div className="space-y-6">
				<PublicPortfolioHeader
					designer={portfolio.designer}
					templateName={templateDef.name}
					templateSummary={templateDef.changesSummary}
					signedIn={isAuthenticated()}
					projectCount={portfolio.projects.length}
				/>

				<ReviewSummarySection
					title="Client Reviews"
					summary={reviews.summary}
					reviews={reviews.reviews}
					emptyMessage="No homeowner reviews yet."
					loadFailed={
						reviewsFailed
							? {
									message: t("reviews.loadFailed"),
									retryLabel: t("reviews.retry"),
									onRetry: () => {
										setReviewsFailed(false);
										fetchDesignerReviews(portfolio.designer.id)
											.then(setReviews)
											.catch((err: unknown) => {
												console.error("Failed to fetch designer reviews:", err);
												setReviewsFailed(true);
											});
									},
								}
							: undefined
					}
				/>

				{renderPublicTemplateSection(
					portfolio.portfolio.template,
					portfolio.projects,
					openProject,
					loadingProjectId,
				)}

				{selected ? (
					<ProjectDetailsModal
						data={selected}
						onClose={closeModal}
						onRequireAuth={requireAuth}
					/>
				) : null}
			</div>
		</div>
	);
}
